/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import {
  computed,
  effect,
  EffectRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import {
  CompanyDto,
  Equipment,
  FileDto,
  ForemanCustomerContact,
  ForemanDailyTask,
  ForemanEmployeeDispatch,
  ForemanEmployeeInOut,
  ForemanEmployeeLabor,
  ForemanEquipmentDispatch,
  ForemanEquipmentUsage,
  ForemanJobCrew,
  ForemanJobDetail,
  ForemanMaterialExpendable,
  ForemanPhase,
  ForemanTask,
  ForemanTimeSheet,
  ForemanUserSignatureDto,
  HoursWorked,
  WorkOrderSummary,
} from '@ups/xplat/api/dto';
import {
  environment,
  Guid,
  LogService,
  MyHttpClientFactory,
  OfflineStorageService,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { ArrayMergeSchema, setCustomCachedEndpoints } from '@ups/xplat/utils';
import moment from 'moment';
import { catchError, Observable, of } from 'rxjs';
import {
  createForemanCrew,
  CreateForemanCrewResult,
  createWorkOrder,
  downloadPdfByFileId,
  fetchCompanies,
  getMaterialExpendables,
  getNextShiftTasks,
  getTasks,
  isChanged,
  setPdfFile,
  updateEmployee,
  updateEquipment,
  updateFavoriteTasks,
  updateMaterials,
  updateTasks,
} from './foreman-job-collections.utils';
import {
  getForemanJobList,
  mergeJobsCachedPriorityMut as mergeForemanJobsCachedWithRemote,
  persistSelectedJobSnapshot,
} from './foreman-job-cache.utils';
import { buildWorkOrderSummaries } from './foreman-job-summary.utils';
import {
  recalculateEquipmentTotalHours,
  updateSelectedEquipment,
} from './foreman-job-equipment-time-entry.utils';
import {
  bulkUpdateLabors as bulkUpdateLaborsUtil,
  copyPreviousDay,
  updateEmployeeLabors as updateEmployeeLaborsUtil,
} from './foreman-job-labor-time-entry.utils';
import { markForemanLaborDraftChanged } from './foreman-labor-draft.utils';
import { submitIndividualTime, submitTime } from './foreman-job-submit.utils';
import { removeEmployeeWorkOrderSelection } from './foreman-job-work-order.utils';
import type { RemoteLaborDraftReplacement } from './foreman-job-remote-labor-reconcile.utils';
import {
  FOREMAN_JOB_LIST_API_ENDPOINT,
  foremanJobsCacheKey,
} from './foreman-job-cache-purge.utils';

@Injectable({
  providedIn: 'root',
})
export class ForemanJobService {
  httpFactory = inject(MyHttpClientFactory);
  log = inject(LogService);
  offlineStorage = inject(OfflineStorageService);
  http: HttpClient;
  httpPascalCase: HttpClient;

  jobListApiEndpoint = FOREMAN_JOB_LIST_API_ENDPOINT;
  // #33947: getter, not field: the user scope resolves after construction.
  get jobsCacheKey(): string {
    return foremanJobsCacheKey(environment.urls.spartaAPI);
  }
  selectedJob = signal<ForemanJobDetail | null>(null);
  selectedDate = signal<Date | null>(null);
  selectedShift = signal<number>(1);
  selectedForemanCrewID = signal<string>(null);
  selectedEmployeeId = signal<string>(null);
  selectedEquipmentId = signal<string>(null);
  remoteLaborDraftReplacements = signal<RemoteLaborDraftReplacement[]>([]);
  pdfFile = signal<FileDto | null>(null);
  customerData = signal<{ firstName: string; lastName: string; email: string }>(
    null
  );
  purchaseOrderNumbers = signal<string[]>([]);
  selectedPurchaseOrderNumber = signal<string | null>(null);
  /**
   * Per-job Job Owner default (#30008). undefined = never touched this session
   * (submit omits the envelope, stored owner untouched); { hrRef: null } = clear.
   */
  selectedJobOwner = signal<{ hrRef: number | null } | undefined>(undefined);
  favoriteTasks = signal<ForemanTask[]>(null);
  favoriteTaskMap = new Map<string, ForemanTask>();
  canGoBack = signal(false);
  canGoForward = signal(false);
  classes: string[] = [];
  absentReasons: { id: string; name: string }[] = [];
  equipment: Equipment[] = [];
  companies: CompanyDto[] = [];

  jobChangeEffect: EffectRef;
  getPersistedJobs: () => ForemanJobDetail[] | null;
  persistJobChanges: (jobs: ForemanJobDetail[]) => void;

  selectedEmployee = computed(() => {
    const job = this.selectedJob();
    const id = this.selectedEmployeeId();
    return id && job
      ? job.employees.find((e) => this.getEmployeeId(e) === id)
      : null;
  });

  selectedEmployeeLabors = computed(() => {
    const employee = this.selectedEmployee();
    const date = this.selectedDate();
    const shift = this.selectedShift();
    const foremanJobCrewID = this.selectedForemanCrewID();

    if (!employee || !employee.labors || !date) {
      return [];
    }

    return employee.labors.filter(
      (l) =>
        l.bActive === true &&
        moment(l.dateWorked).format('MM/DD/YYYY') ===
          moment(date).format('MM/DD/YYYY') &&
        (!shift || l.shift === shift) &&
        l.foremanJobCrewID === foremanJobCrewID
    );
  });

  selectedEquipment = computed(() => {
    const job = this.selectedJob();
    const id = this.selectedEquipmentId();
    return id && job
      ? job.equipment.find((e) => this.getEquipmentId(e) === id)
      : null;
  });

  selectedEquipmentUsages = computed(() =>
    this.getEquipmentUsagesForDate(this.selectedEquipment())
  );

  workOrderSummaries = computed<WorkOrderSummary[]>(() =>
    buildWorkOrderSummaries(this.selectedJob())
  );

  jobMergeSchema: ArrayMergeSchema = {
    employees: {
      key: (e: ForemanEmployeeDispatch) => e.foremanEmployeeJobID,
      children: {
        labors: { key: (l: ForemanEmployeeLabor) => l.foremanEmployeeLaborID },
      },
    },
    equipment: {
      key: (eq: ForemanEquipmentDispatch) => eq.foremanEquipmentDispatchID,
      children: {
        usages: {
          key: (u: ForemanEquipmentUsage) => u.foremanEquipmentUsageID,
        },
      },
    },
    workOrders: { key: (w: ForemanPhase) => w.foremanPhaseID },
    foremanCrews: { key: (c: ForemanJobCrew) => c.foremanJobCrewID },
    tasks: { key: (t: ForemanDailyTask) => t.foremanDailyTaskID },
    materialExpendables: {
      key: (m: ForemanMaterialExpendable) => m.foremanMaterialExpendableID,
    },
    dailySafetyItems: { key: (d: any) => d.foremanDailySafetyItemID },
  };

  getEmployeesForTimeEntry = computed<ForemanEmployeeDispatch[]>(() => {
    const job = this.selectedJob();
    const date = this.selectedDate();
    const shift = this.selectedShift();

    if (!job || !date) {
      return [];
    }

    const employees = job.employees.filter(
      (e) =>
        e.bActive !== false &&
        (e.dispatchStatus !== 'ROF' ||
          (!e.rofDate && new Date(e.rofDate) > date)) &&
        (!shift || !e.shift || e.shift === shift) &&
        this.isEmployeeAssignedToSelectedCrew(e)
    );

    for (const employee of employees) {
      this.calculateTotalHours(employee, date);
    }

    return employees;
  });

  getEquipmentForTimeEntry = computed<ForemanEquipmentDispatch[]>(() => {
    const job = this.selectedJob();
    const date = this.selectedDate();
    const shift = this.selectedShift();

    if (!job || !date) {
      return [];
    }

    const equipment = (job.equipment || []).filter(
      (e) =>
        e.bActive !== false &&
        this.isOnOrBefore(e.startDate, date) &&
        this.isOnOrAfter(e.endDate, date) &&
        this.isOnOrAfter(e.lastDayOnSite, date) &&
        (!shift || !e.shift || e.shift === shift) &&
        this.isEquipmentAssignedToSelectedCrew(e)
    );

    for (const item of equipment) {
      this.calculateTotalHoursUsed(item);
    }

    return equipment;
  });

  currentTimeSheet = computed<ForemanTimeSheet>(() => {
    const selectedJob = this.selectedJob();
    const selectedDate = this.selectedDate();
    const selectedShift = this.selectedShift() ? this.selectedShift() : null;
    const selectedForemanCrewID = this.selectedForemanCrewID();

    return (selectedJob.timeSheets || []).find(
      (t) =>
        moment(t.workDate).format('MM/DD/YYYY') ===
          moment(selectedDate).format('MM/DD/YYYY') &&
        t.shift === selectedShift &&
        t.foremanJobCrewID === selectedForemanCrewID
    );
  });

  selectedShiftName = computed<string>(() => {
    const shift = this.selectedShift();
    return shift === 1 ? 'Day' : shift === 2 ? 'Night' : 'Both';
  });

  selectedForemanCrew = computed(() => {
    const selectedJob = this.selectedJob();
    const selectedForemanCrewID = this.selectedForemanCrewID();
    return selectedJob?.foremanCrews?.find(
      (c) => c.foremanJobCrewID === selectedForemanCrewID
    );
  });

  private _ignoreCache = false;

  constructor() {
    const url = environment.urls.spartaAPI;
    this.http = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.CamelCase
    );
    this.httpPascalCase = this.httpFactory.createHttpClient(
      url,
      true,
      ResponseCasingEnum.PascalCase
    );
    setCustomCachedEndpoints([this.jobsCacheKey]);

    this.jobChangeEffect = effect(() => {
      const selectedJob = this.selectedJob();
      if (!this._ignoreCache) {
        this.persistSelectedJobSnapshot(selectedJob);
      }
      this._ignoreCache = false;

      // A Job Owner envelope belongs to ONE job's submission screen; a stale
      // envelope carried across a job switch would rewrite the next job's owner.
      this.selectedJobOwner.set(undefined);

      if (selectedJob?.favoriteTasks && !this.favoriteTasks()) {
        this.setFavoriteTasks(selectedJob.favoriteTasks);
      }

      if (selectedJob?.vpJobID) {
        this.loadPurchaseOrderNumbers(selectedJob.vpJobID);
      } else {
        this.purchaseOrderNumbers.set([]);
        this.selectedPurchaseOrderNumber.set(null);
      }
    });
  }

  getEquipmentUsagesForDate(
    equipment: ForemanEquipmentDispatch | null | undefined
  ): ForemanEquipmentUsage[] {
    const date = this.selectedDate();
    const shift = this.selectedShift();
    const foremanJobCrewID = this.selectedForemanCrewID();

    if (!equipment || !equipment.usages || !date) {
      return [];
    }

    return equipment.usages.filter(
      (u) =>
        u.bActive === true &&
        moment(u.dateUsed).format('MM/DD/YYYY') ===
          moment(date).format('MM/DD/YYYY') &&
        (!shift || u.shift === shift) &&
        u.foremanJobCrewID === foremanJobCrewID
    );
  }

  getWorkOrderById(foremanPhaseID?: string | null): ForemanPhase | undefined {
    return foremanPhaseID
      ? (this.selectedJob()?.workOrders || []).find(
          (wo) => wo.foremanPhaseID === foremanPhaseID
        )
      : undefined;
  }

  formatWorkOrderLabel(phaseName?: string | null, description?: string | null) {
    const label = (phaseName || '').trim();
    const detail = (description || '').trim();
    return label && detail ? `${label} - ${detail}` : label || detail;
  }

  formatWorkOrderLabelById(
    foremanPhaseID?: string | null,
    fallbackPhaseName?: string | null
  ) {
    const workOrder = this.getWorkOrderById(foremanPhaseID);
    return this.formatWorkOrderLabel(
      workOrder?.phaseName || fallbackPhaseName,
      workOrder?.description
    );
  }

  isEmployeeAssignedToSelectedCrew(e: ForemanEmployeeDispatch) {
    const foremanCrewID = this.selectedForemanCrewID();
    const assignment = (e.foremanCrews ?? []).find(
      (c) => c.foremanJobCrewID === foremanCrewID
    );
    return !foremanCrewID || !!assignment?.bActive;
  }

  isEquipmentAssignedToSelectedCrew(e: ForemanEquipmentDispatch) {
    const foremanCrewID = this.selectedForemanCrewID();
    const assignment = (e.foremanCrews ?? []).find(
      (c) => c.foremanJobCrewID === foremanCrewID
    );
    return !foremanCrewID || !assignment || assignment.bActive === true;
  }

  selectEmployee(employee: ForemanEmployeeDispatch) {
    this.selectedEmployeeId.set(employee ? this.getEmployeeId(employee) : null);
  }

  setFavoriteTasks(favoriteTasks: ForemanTask[]) {
    favoriteTasks = favoriteTasks || [];
    this.favoriteTasks.set(favoriteTasks || []);
    this.favoriteTaskMap = new Map(
      favoriteTasks.map((i) => [i.foremanTaskID, i])
    );
  }

  getForemanJobDetail(jobID: string): Observable<ForemanJobDetail> {
    this._ignoreCache = true;
    return this.http.get(
      `/api/foreman/job/${jobID}/detail`
    ) as Observable<ForemanJobDetail>;
  }

  createForemanJob(job: ForemanJobDetail): Observable<ForemanJobDetail> {
    this._ignoreCache = true;
    const url = '/api/foreman/job';
    return this.http.post(url, job) as Observable<ForemanJobDetail>;
  }

  calculateTotalHours(employee: ForemanEmployeeDispatch, date: Date) {
    const shift = this.selectedShift();
    const selectedForemanCrewID = this.selectedForemanCrewID();

    const labors = (employee.labors || []).filter(
      (l) =>
        moment(l.dateWorked).format('MM/DD/YYYY') ===
          moment(date).format('MM/DD/YYYY') &&
        (!shift || !l.shift || l.shift === shift) &&
        (l.foremanJobCrewID == null ||
          l.foremanJobCrewID === selectedForemanCrewID)
    );

    const earnCodes = ['ST', 'OT', 'PT'];

    employee.totalHoursWorked = labors
      .filter((l) => !!l.bActive && earnCodes.includes(l.foremanEarnCode))
      .reduce((acc, n) => acc + Number(n.hours || 0), 0);
  }

  calculateTotalHoursUsed(equipment: ForemanEquipmentDispatch) {
    const date = this.selectedDate();
    const shift = this.selectedShift();
    const selectedForemanCrewID = this.selectedForemanCrewID();

    equipment.totalHoursUsed = (equipment.usages || [])
      .filter(
        (l) =>
          moment(l.dateUsed).format('MM/DD/YYYY') ===
            moment(date).format('MM/DD/YYYY') &&
          (!shift || l.shift === shift) &&
          l.foremanJobCrewID === selectedForemanCrewID &&
          !!l.bActive &&
          !l.notUsed
      )
      .reduce((acc, n) => {
        const qty = Number(n.quantity || 1);
        const amount = Number(n.amount || 0);
        return acc + qty * amount;
      }, 0);
  }

  setDefaultEmployeeWorkOrders(
    workOrderIds: string | string[],
    overwriteExisting?: boolean
  ) {
    const currentJob = this.selectedJob();
    if (!currentJob || !currentJob.employees) return;

    // Normalize to array
    const ids = Array.isArray(workOrderIds)
      ? workOrderIds
      : [workOrderIds].filter(Boolean);

    const workOrders = ids
      .map((id) => currentJob.workOrders.find((w) => w.foremanPhaseID === id))
      .filter(Boolean);

    for (const e of currentJob.employees) {
      if (overwriteExisting) {
        // Replace all existing assignments
        e.foremanPhaseIDs = workOrders.map((w) => w.foremanPhaseID);
        e.foremanPhaseNames = workOrders.map((w) => w.phaseName);
      } else {
        // Additive: merge with existing
        const existingIds =
          e.foremanPhaseIDs || (e.foremanPhaseID ? [e.foremanPhaseID] : []);
        const newIds = workOrders
          .map((w) => w.foremanPhaseID)
          .filter((id) => !existingIds.includes(id));

        e.foremanPhaseIDs = [...existingIds, ...newIds];
        e.foremanPhaseNames = e.foremanPhaseIDs
          .map(
            (id) =>
              currentJob.workOrders.find((w) => w.foremanPhaseID === id)
                ?.phaseName
          )
          .filter(Boolean);
      }

      // Keep backward compatibility - set primary (first) work order
      e.foremanPhaseID = e.foremanPhaseIDs?.[0] || null;
      e.foremanPhaseName = e.foremanPhaseNames?.[0] || null;
    }

    this.selectedJob.set({ ...currentJob });
  }

  setDefaultEquipmentWorkOrders(
    workOrderIds: string | string[],
    overwriteExisting?: boolean
  ) {
    const currentJob = this.selectedJob();
    if (!currentJob || !currentJob.equipment) return;

    // Normalize to array
    const ids = Array.isArray(workOrderIds)
      ? workOrderIds
      : [workOrderIds].filter(Boolean);

    const workOrders = ids
      .map((id) => currentJob.workOrders.find((w) => w.foremanPhaseID === id))
      .filter(Boolean);

    for (const e of currentJob.equipment) {
      if (overwriteExisting) {
        // Replace all existing assignments
        e.foremanPhaseIDs = workOrders.map((w) => w.foremanPhaseID);
        e.foremanPhaseNames = workOrders.map((w) => w.phaseName);
      } else {
        // Additive: merge with existing
        const existingIds =
          e.foremanPhaseIDs || (e.foremanPhaseID ? [e.foremanPhaseID] : []);
        const newIds = workOrders
          .map((w) => w.foremanPhaseID)
          .filter((id) => !existingIds.includes(id));

        e.foremanPhaseIDs = [...existingIds, ...newIds];
        e.foremanPhaseNames = e.foremanPhaseIDs
          .map(
            (id) =>
              currentJob.workOrders.find((w) => w.foremanPhaseID === id)
                ?.phaseName
          )
          .filter(Boolean);
      }

      // Keep backward compatibility - set primary (first) work order
      e.foremanPhaseID = e.foremanPhaseIDs?.[0] || null;
      e.foremanPhaseName = e.foremanPhaseNames?.[0] || null;
    }

    this.selectedJob.set({ ...currentJob });
  }

  setEquipmentWorkOrder(
    equipment: ForemanEquipmentDispatch,
    workOrderIds: string | string[],
    overwriteExisting = false
  ) {
    const currentJob = this.selectedJob();

    // Normalize to array
    const ids = Array.isArray(workOrderIds)
      ? workOrderIds
      : [workOrderIds].filter(Boolean);

    const workOrders = ids
      .map((id) => currentJob.workOrders.find((w) => w.foremanPhaseID === id))
      .filter(Boolean);

    if (overwriteExisting || !equipment.foremanPhaseIDs?.length) {
      equipment.foremanPhaseIDs = workOrders.map((w) => w.foremanPhaseID);
      equipment.foremanPhaseNames = workOrders.map((w) => w.phaseName);
    } else {
      // Additive
      const existingIds = equipment.foremanPhaseIDs || [];
      const newIds = workOrders
        .map((w) => w.foremanPhaseID)
        .filter((id) => !existingIds.includes(id));

      equipment.foremanPhaseIDs = [...existingIds, ...newIds];
      equipment.foremanPhaseNames = equipment.foremanPhaseIDs
        .map(
          (id) =>
            currentJob.workOrders.find((w) => w.foremanPhaseID === id)
              ?.phaseName
        )
        .filter(Boolean);
    }

    // Backward compatibility
    equipment.foremanPhaseID = equipment.foremanPhaseIDs?.[0] || null;
    equipment.foremanPhaseName = equipment.foremanPhaseNames?.[0] || null;
  }

  setEmployeeWorkOrder(
    employee: ForemanEmployeeDispatch,
    workOrderIds: string | string[],
    overwriteExisting = false
  ) {
    const currentJob = this.selectedJob();

    // Normalize to array
    const ids = Array.isArray(workOrderIds)
      ? workOrderIds
      : [workOrderIds].filter(Boolean);

    const workOrders = ids
      .map((id) => currentJob.workOrders.find((w) => w.foremanPhaseID === id))
      .filter(Boolean);

    if (overwriteExisting || !employee.foremanPhaseIDs?.length) {
      employee.foremanPhaseIDs = workOrders.map((w) => w.foremanPhaseID);
      employee.foremanPhaseNames = workOrders.map((w) => w.phaseName);
    } else {
      // Additive
      const existingIds = employee.foremanPhaseIDs || [];
      const newIds = workOrders
        .map((w) => w.foremanPhaseID)
        .filter((id) => !existingIds.includes(id));

      employee.foremanPhaseIDs = [...existingIds, ...newIds];
      employee.foremanPhaseNames = employee.foremanPhaseIDs
        .map(
          (id) =>
            currentJob.workOrders.find((w) => w.foremanPhaseID === id)
              ?.phaseName
        )
        .filter(Boolean);
    }

    // Backward compatibility
    employee.foremanPhaseID = employee.foremanPhaseIDs?.[0] || null;
    employee.foremanPhaseName = employee.foremanPhaseNames?.[0] || null;
  }

  bulkUpdateEquipment(workOrderId: string, shift: number, hours: number) {
    const currentJob = this.selectedJob();
    const equipments = this.getEquipmentForTimeEntry();

    for (const equipment of equipments) {
      const foremanPhaseID = workOrderId || equipment.foremanPhaseID;

      if (!foremanPhaseID) continue;

      this.updateEquipmentUsages(
        equipment,
        foremanPhaseID,
        shift,
        hours,
        null,
        true
      );
    }

    this.selectedJob.set({ ...currentJob });
  }

  updateEquipmentUsages(
    equipment: ForemanEquipmentDispatch,
    foremanPhaseID: string,
    shift: number,
    hours: number,
    notes: string,
    isBulk?: boolean
  ) {
    if (!equipment.usages) {
      equipment.usages = [];
    }

    if (!equipment.foremanPhaseID) {
      equipment.foremanPhaseID = foremanPhaseID;
      const workOrder = this.getWorkOrderById(foremanPhaseID);
      equipment.foremanPhaseName =
        workOrder?.phaseName || equipment.foremanPhaseName;
    }

    let usage = (equipment.usages || []).find(
      (l) =>
        moment(l.dateUsed).format('MM/DD/YYYY') ===
          moment(this.selectedDate()).format('MM/DD/YYYY') &&
        l.shift === shift &&
        l.foremanJobCrewID === this.selectedForemanCrewID() &&
        l.foremanPhaseID === foremanPhaseID
    );

    if (!usage && hours > 0) {
      usage = {
        foremanEquipmentUsageID: Guid.newGuid(),
        foremanEquipmentDispatchID: equipment.foremanEquipmentDispatchID,
        dateUsed: this.selectedDate(),
        foremanPhaseID: foremanPhaseID,
        shift: shift,
        foremanJobCrewID: this.selectedForemanCrewID(),
        quantity: 1,
        isChanged: true,
      } as ForemanEquipmentUsage;

      equipment.usages.push(usage);
    }

    if (usage) {
      const newAmount = hours || usage.amount;
      const newActive = hours > 0;

      usage.isChanged =
        usage.isChanged ||
        usage.amount !== newAmount ||
        usage.foremanNotes !== notes ||
        usage.bActive !== newActive;

      usage.amount = newAmount;
      usage.bActive = newActive;

      if (!isBulk) {
        usage.foremanNotes = notes;
      }
    }

    this.calculateTotalHoursUsed(equipment);

    if (!isBulk) {
      this.updateEquipment(equipment);
    }
  }

  setNotUsed(equipment: ForemanEquipmentDispatch) {
    if (!equipment.usages) {
      equipment.usages = [];
    }

    const selectedForemanCrewID = this.selectedForemanCrewID();
    const usages = this.getEquipmentUsagesForDate(equipment);

    usages.forEach((u) => {
      if (u.bActive) {
        u.bActive = false;
        u.isChanged = true;
      }
    });

    let notUsedUsageRecord = usages.find((u) => u.notUsed === true);

    if (!notUsedUsageRecord) {
      notUsedUsageRecord = {
        foremanEquipmentUsageID: Guid.newGuid(),
        foremanEquipmentDispatchID: equipment.foremanEquipmentDispatchID,
        dateUsed: this.selectedDate(),
        foremanJobCrewID: selectedForemanCrewID,
        shift: this.selectedShift(),
        isChanged: true,
      } as ForemanEquipmentUsage;

      equipment.usages.push(notUsedUsageRecord);
    } else {
      // Only set isChanged if status is actually changing
      const statusChanged =
        notUsedUsageRecord.bActive !== true ||
        notUsedUsageRecord.notUsed !== true;
      if (statusChanged) {
        notUsedUsageRecord.isChanged = true;
      }
    }

    notUsedUsageRecord.bActive = true;
    notUsedUsageRecord.notUsed = true;

    this.calculateTotalHoursUsed(equipment);

    this.updateEquipment(equipment);
  }

  clearNotUsed(equipment: ForemanEquipmentDispatch) {
    if (!equipment.usages) {
      equipment.usages = [];
    }

    const usages = this.selectedEquipmentUsages();
    usages.forEach((u) => {
      if (u.notUsed === true && u.bActive) {
        u.bActive = false;
        u.isChanged = true;
      }
    });

    this.calculateTotalHoursUsed(equipment);

    this.updateEquipment(equipment);
  }

  setAbsentReasonOther(
    employee: ForemanEmployeeDispatch,
    absentReasonOther: string
  ) {
    const otherReason = this.absentReasons.find((a) => a.name === 'Other');

    this.setAbsent(employee, otherReason.id, absentReasonOther);
  }

  setAbsent(
    employee: ForemanEmployeeDispatch,
    absentReasonID: string,
    absentReasonOther: string
  ) {
    if (!employee.labors) {
      employee.labors = [];
    }

    const labors = employee.labors.filter(
      (l) =>
        moment(l.dateWorked).format('MM/DD/YYYY') ===
        moment(this.selectedDate()).format('MM/DD/YYYY')
    );

    labors.forEach((l) => {
      if (l.bActive) {
        l.bActive = false;
        markForemanLaborDraftChanged(l);
      }
    });

    let absentRecord = labors.find((l) => l.isAbsent === true);

    if (!absentRecord) {
      absentRecord = {
        foremanEmployeeLaborID: Guid.newGuid(),
        foremanEmployeeJobID: employee.foremanEmployeeJobID,
        dateWorked: this.selectedDate(),
        foremanEarnCode: 'ST',
        isChanged: true,
      } as ForemanEmployeeLabor;
      markForemanLaborDraftChanged(absentRecord);

      employee.labors.push(absentRecord);
    } else {
      // Only set isChanged if the absent reason is actually changing
      const reasonChanged =
        absentRecord.absentReasonID !== absentReasonID ||
        absentRecord.absentReasonOther !== absentReasonOther ||
        absentRecord.bActive !== true;
      if (reasonChanged) {
        markForemanLaborDraftChanged(absentRecord);
      }
    }

    const absentReason =
      this.absentReasons.find((a) => a.id === absentReasonID) ||
      this.absentReasons[0];

    absentRecord.bActive = true;
    absentRecord.isAbsent = true;
    absentRecord.absentReasonID = absentReasonID;
    absentRecord.absentReason = absentReason.name;
    absentRecord.absentReasonOther = absentReasonOther;

    this.calculateTotalHours(employee, this.selectedDate());

    // Force the selectedJob signal to update so work order summaries will recalculate
    const currentJob = this.selectedJob();
    this.selectedJob.set({ ...currentJob });
  }

  clearAbsent(employee: ForemanEmployeeDispatch) {
    if (!employee.labors) {
      employee.labors = [];
    }

    const labors = employee.labors.filter(
      (l) =>
        moment(l.dateWorked).format('MM/DD/YYYY') ===
        moment(this.selectedDate()).format('MM/DD/YYYY')
    );

    const absentRecord = labors.find((l) => l.isAbsent === true);

    if (absentRecord && absentRecord.bActive) {
      absentRecord.bActive = false;
      markForemanLaborDraftChanged(absentRecord);
    }

    this.calculateTotalHours(employee, this.selectedDate());

    const currentJob = this.selectedJob();
    this.selectedJob.set({ ...currentJob });
  }

  /**
   * Creates a foreman (temporary) job using the API create-job payload shape.
   * Use this when building the payload from the Create Temporary Job form (e.g. buildCreateJobDto).
   */
  createForemanJobWithPayload(payload: object): Observable<ForemanJobDetail> {
    this._ignoreCache = true;
    const url = '/api/foreman/job';
    return this.http.post(url, payload) as Observable<ForemanJobDetail>;
  }

  loadPurchaseOrderNumbers(jobNumber: string): void {
    if (!jobNumber) {
      this.purchaseOrderNumbers.set([]);
      return;
    }

    this.http
      .get<string[]>(`/api/foreman/job/${jobNumber}/purchase-order-numbers`)
      .pipe(
        catchError((error) => {
          this.log.error('Error loading purchase order numbers', error);
          this.purchaseOrderNumbers.set([]);
          return of([]);
        })
      )
      .subscribe((poNumbers) => {
        this.purchaseOrderNumbers.set(poNumbers);
      });
  }

  getCustomerContacts(
    foremanJobID: string
  ): Observable<ForemanCustomerContact[]> {
    return this.http.get(
      `/api/foreman/job/${foremanJobID}/customer-contacts`
    ) as Observable<ForemanCustomerContact[]>;
  }

  removeCustomerContact(
    foremanJobID: string,
    foremanCustomerContactID: string
  ): Observable<void> {
    return this.http.delete(
      `/api/foreman/job/${foremanJobID}/customer-contacts/${foremanCustomerContactID}`
    ) as unknown as Observable<void>;
  }

  /**
   * The signed-in user's saved signature, or null if none is stored.
   * Self-scoped server-side to the caller (no id param).
   */
  getUserSignature(): Observable<ForemanUserSignatureDto | null> {
    return this.http.get(
      '/api/foreman/user-signature'
    ) as Observable<ForemanUserSignatureDto | null>;
  }

  /** Upsert the signed-in user's saved signature (bare base64, no data-URI prefix). */
  saveUserSignature(dto: ForemanUserSignatureDto): Observable<void> {
    return this.http.put(
      '/api/foreman/user-signature',
      dto
    ) as unknown as Observable<void>;
  }

  getEquipment(jobID: string, companyID: string) {
    this.loadEquipmentOptions(jobID, companyID).subscribe({
      next: (data: Equipment[]) => {
        this.equipment = data;
      },
    });
  }

  loadEquipmentOptions(
    jobID: string,
    companyID: string
  ): Observable<Equipment[]> {
    let url = `/api/equipments/${jobID}`;
    if (companyID) {
      url += `?companyID=${companyID}`;
    }

    return this.http.get(url) as Observable<Equipment[]>;
  }

  getEmployeeId(employee: ForemanEmployeeDispatch): string {
    return employee?.employeeJobID || employee?.foremanEmployeeJobID;
  }

  getEquipmentId(eq: ForemanEquipmentDispatch): string {
    return eq.equipmentDispatchID ?? eq.foremanEquipmentDispatchID;
  }

  declare getForemanJobList: () => Observable<ForemanJobDetail[]>;
  declare mergeJobsCachedPriorityMut: (
    cachedJobs: ForemanJobDetail[],
    remoteJobs: ForemanJobDetail[]
  ) => ForemanJobDetail[];
  declare persistSelectedJobSnapshot: (
    selectedJobOverride?: ForemanJobDetail | null
  ) => void;
  declare removeEmployeeWorkOrderSelection: (
    employee: ForemanEmployeeDispatch,
    workOrderId: string
  ) => void;
  declare submitTime: () => Observable<ForemanJobDetail>;
  declare submitIndividualTime: (
    jobs: ForemanJobDetail[],
    spartaEmployeeId?: string | null
  ) => Observable<ForemanJobDetail[]>;
  declare getTasks: () => ForemanDailyTask[];
  declare getNextShiftTasks: () => ForemanDailyTask[];
  declare updateTasks: (tasks: ForemanDailyTask[]) => void;
  declare updateFavoriteTasks: (tasks: ForemanDailyTask[]) => void;
  declare getMaterialExpendables: () => ForemanMaterialExpendable[];
  declare updateMaterials: (materials: ForemanMaterialExpendable[]) => void;
  declare copyPreviousDay: () => void;
  declare bulkUpdateLabors: (
    workOrderId: string,
    shift: number,
    hoursWorked: HoursWorked
  ) => void;
  declare updateEmployeeLabors: (
    currentJob: ForemanJobDetail,
    employee: ForemanEmployeeDispatch,
    foremanPhaseID: string,
    shift: number,
    hoursWorked: HoursWorked,
    notes: string,
    inOuts: ForemanEmployeeInOut[],
    isBulk?: boolean,
    isException?: boolean,
    exceptionNotes?: string | null
  ) => void;
  declare createWorkOrder: (
    workOrder: string,
    description: string
  ) => ForemanPhase;
  declare createForemanCrew: (
    id: string,
    foremanCrewName: string,
    shift?: number
  ) => CreateForemanCrewResult | null;
  declare updateEmployee: (employee: ForemanEmployeeDispatch) => void;
  declare updateEquipment: (equipment: ForemanEquipmentDispatch) => void;
  declare recalculateEquipmentTotalHours: () => void;
  declare updateSelectedEquipment: (
    equipmentDispatchID: string,
    updates: Partial<ForemanEquipmentDispatch>
  ) => void;
  declare fetchCompanies: () => Observable<CompanyDto[]>;
  declare setPdfFile: (file: FileDto) => void;
  declare downloadPdfByFileId: (fileId: string) => Observable<any>;
  declare isChanged: () => boolean;

  private isOnOrBefore(
    value: Date | string | null | undefined,
    compareTo: Date
  ): boolean {
    const parsed = this.parseDateValue(value);
    return !parsed || parsed.isSameOrBefore(moment(compareTo), 'day');
  }

  private isOnOrAfter(
    value: Date | string | null | undefined,
    compareTo: Date
  ): boolean {
    const parsed = this.parseDateValue(value);
    return !parsed || parsed.isSameOrAfter(moment(compareTo), 'day');
  }

  private parseDateValue(
    value: Date | string | null | undefined
  ): moment.Moment | null {
    if (!value) {
      return null;
    }

    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
  }
}

Object.assign(ForemanJobService.prototype, {
  getForemanJobList() {
    return getForemanJobList(this);
  },
  mergeJobsCachedPriorityMut(cachedJobs, remoteJobs) {
    return mergeForemanJobsCachedWithRemote(
      this.jobMergeSchema,
      cachedJobs,
      remoteJobs
    );
  },
  persistSelectedJobSnapshot(selectedJobOverride) {
    persistSelectedJobSnapshot(this, selectedJobOverride);
  },
  removeEmployeeWorkOrderSelection(employee, workOrderId) {
    removeEmployeeWorkOrderSelection(this, employee, workOrderId);
  },
  submitTime() {
    return submitTime(this);
  },
  submitIndividualTime(jobs, spartaEmployeeId) {
    return submitIndividualTime(this, jobs, spartaEmployeeId);
  },
  getTasks() {
    return getTasks(this);
  },
  getNextShiftTasks() {
    return getNextShiftTasks(this);
  },
  updateTasks(tasks) {
    updateTasks(this, tasks);
  },
  updateFavoriteTasks(tasks) {
    updateFavoriteTasks(this, tasks);
  },
  getMaterialExpendables() {
    return getMaterialExpendables(this);
  },
  updateMaterials(materials) {
    updateMaterials(this, materials);
  },
  copyPreviousDay() {
    copyPreviousDay(this);
  },
  bulkUpdateLabors(workOrderId, shift, hoursWorked) {
    bulkUpdateLaborsUtil(this, workOrderId, shift, hoursWorked);
  },
  updateEmployeeLabors(
    currentJob,
    employee,
    foremanPhaseID,
    shift,
    hoursWorked,
    notes,
    inOuts,
    isBulk,
    isException,
    exceptionNotes
  ) {
    updateEmployeeLaborsUtil(
      this,
      currentJob,
      employee,
      foremanPhaseID,
      shift,
      hoursWorked,
      notes,
      inOuts,
      isBulk,
      isException,
      exceptionNotes
    );
  },
  createWorkOrder(workOrder, description) {
    return createWorkOrder(this, workOrder, description);
  },
  createForemanCrew(id, foremanCrewName, shift = null) {
    return createForemanCrew(this, id, foremanCrewName, shift);
  },
  updateEmployee(employee) {
    updateEmployee(this, employee);
  },
  updateEquipment(equipment) {
    updateEquipment(this, equipment);
  },
  recalculateEquipmentTotalHours() {
    recalculateEquipmentTotalHours(this);
  },
  updateSelectedEquipment(equipmentDispatchID, updates) {
    updateSelectedEquipment(this, equipmentDispatchID, updates);
  },
  fetchCompanies() {
    return fetchCompanies(this);
  },
  setPdfFile(file) {
    setPdfFile(this, file);
  },
  downloadPdfByFileId(fileId) {
    return downloadPdfByFileId(this, fileId);
  },
  isChanged() {
    return isChanged(this);
  },
});
